import _ from "lodash";
import Joi from 'joi';

export const IMAGE_LIMITS = {
	ANOMALY_LOCATIONS: 60,
	GENERAL_PHOTOS: 75,
	EXCEL_ISO_UPLOAD: 1,
	ISO_IMAGE: 10,
	PID_IMAGE: 3,
	SCAN_PLAN: 15,
	SCAN_IMAGE: 1,
	DRT_PHOTO: 1,
	PCNL_CLASSIFICATION: 1,
	PAUT_SCAN_PLAN: 1,
};

export const TABLE_ITEM_COUNT_LIMITS = {
	PEC: 15,
	EFVIS: 15,
	EFVIP: 11,
	ERISER: 10,
	PCNL: 15,
	MPI: 8,
	LPT: 8,
	DRT: 15,
	UTM: 15,
	CONCRETE: 5,
	ET: 5,
	AUT: 25,
	PMI: 25,
	PCNL_RISK_ASSESSMENT: 5,
};

export const PROJECT_STATUS = {
	AWAITING_ACTIVATION: 'Awaiting Activation',
	PENDING: 'Pending',
	PUBLISHED_FOR_REVIEW: 'Published for Review',
	UNDER_CORRECTION: 'Under Correction',
	PUBLISHED_FOR_INTERNAL_APPROVAL: 'Published for Internal Approval',
	PUBLISHED_FOR_EXTERNAL_REVIEW: 'Published for Customer Review',
	PUBLISHED_FOR_EXTERNAL_APPROVAL: 'Published for Customer Approval',
	COMPLETED: 'Completed',
} as const;

export const MATCH_TYPES = {
	CONTAINS: 'contains',
	IS: 'is',
} as const;

export const USER_TYPES = {
	INTERNAL: 'internal user',
	EXTERNAL: 'external user',
} as const;

export const DAMAGE_CLASS = {
	ZERO: {
		ID: 0,
		COLOR: '#ffffff',
	},
	ONE: {
		ID: 1,
		COLOR: '#ccffff',
	},
	TWO: {
		ID: 2,
		COLOR: '#ccffcc',
	},
	THREE: {
		ID: 3,
		COLOR: '#ffffcc',
	},
	FOUR: {
		ID: 4,
		COLOR: '#ffcccc',
	},
	FIVE: {
		ID: 5,
		COLOR: '#fabf8f',
	},
	SIX: {
		ID: 6,
		COLOR: '#da9694',
	},
};

export const ANOMALY_TYPE = {
	NONE: {
		ID: 'No Anomaly',
		COLOR: 'transparent',
		CLASS: '0',
	},
	MINOR: {
		ID: 'Minor Anomaly',
		COLOR: '#d0e1d3',
		CLASS: 'A',
	},
	MINOR_TO_MODERATE: {
		ID: 'Minor to Moderate Anomaly',
		COLOR: '#fae2d5',
		CLASS: 'B',
	},
	MODERATE: {
		ID: 'Moderate Anomaly',
		COLOR: '#e7e9eb',
		CLASS: 'C',
	},
	MAJOR: {
		ID: 'Major Anomaly / Severe / Adverse Anomaly',
		COLOR: '#d8aad2',
		CLASS: 'D',
	},
};

export const ANOMALY_SUMMARY_OPTIONS = {
	NO_ANOMALY: 'N/A or No Anomaly',
	ANOMALY: 'Anomaly'
};

export const DAMAGE_CLASS_TO_RISK_FACTOR = {
	low: {
		classes: [DAMAGE_CLASS.ONE.ID, DAMAGE_CLASS.TWO.ID],
		color: '#00b050',
		id: 1,
	},
	medium: {
		classes: [DAMAGE_CLASS.THREE.ID, DAMAGE_CLASS.FOUR.ID],
		color: 'yellow',
		id: 2,
	},
	high: { classes: [DAMAGE_CLASS.FIVE.ID], color: '#ffc103', id: 3 },
	severe: { classes: [DAMAGE_CLASS.SIX.ID], color: 'red', id: 4 },
};

export const tutorialConstants = {
	INTRODUCTION: 1,
	WELCOME: 2,
	EXTERNAL_DASHBOARD: 3,
	INTERNAL_DASHBOARD: 4,
	INSPECTION_REPORT: 5,
	ALL_PROJECTS: 6,
	PROJECT_DETAILS: 7,
	REQUEST_SUPERVISOR_UPDATE: 8,
	CREATE_PROJECT: 9,
	EDIT_PROJECT: 10,
	ACTIVATE_PROJECT: 11,
	PREP_IMAGES: 12,
	CREATE_REPORT: 13,
	NEW_REPORT: 14,
	REPORT_TYPE: 15,
	AUTOSAVE_REPORT: 16,
	SECTION_COMPLETED: 17,
	PREVIEW_REPORT: 18,
	VIEW_REPORT: 19,
	REVIEW_REPORT: 20,
	APPROVE_REPORT: 21,
	ACCEPT_REPORT: 22,
	MANAGE_USERS: 23,
	SETUP_DIVISIONS: 24,
	SETUP_PRIVILEGES: 25,
	SETUP_USER: 26,
	MANAGE_ORGANIZATIONS: 27,
	VIEW_PROJECT_PROGRESS: 28,
	VIEW_UNPUBLISHED_REPORT: 29,
	ADD_PROJECT_COMMENT: 30,
	VIEW_PROJECT_COMMENTS: 31,
	PROFILE_SETTINGS: 32,
	VIEW_TUTORIALS: 33,
	DOWNLOAD_USER_GUIDE: 34,
	MAYBE_LATER: 35,
	COMPLETED: 36,
	RESTART_TOTORIALS: 37,
	INACTIVE: 38,
};

export const IMAGE_UPLOAD_FORMATS = [
	'image/apng',
	'image/bmp',
	'image/gif',
	'image/jpeg',
	'image/jpg',
	'image/pjpeg',
	'image/png',
	'image/svg+xml',
	'image/tiff',
	'image/webp',
	'image/x-icon',
];

export const MATERIAL_OPTIONS = [
	{
		id: 'Carbon Steel',
		name: 'Carbon Steel',
		code: 'CS',
		digit: '236',
	},
	{
		id: 'Stainless Steel',
		name: 'Stainless Steel',
		code: 'SS',
		digit: '237',
	},
	{
		id: 'Copper Nickel',
		name: 'Copper Nickel',
		code: 'CN',
		digit: '238',
	},
	{
		id: 'Aluminium',
		name: 'Aluminium',
		code: 'AL',
		digit: '239',
	},
	{
		id: 'Titanium',
		name: 'Titanium',
		code: 'TT',
		digit: '240',
	},
];

export const SCAN_DIRECTIONS = [
	{
		id: 'Single Direction',
		name: 'Single Direction'
	},
	{
		id: 'Bi-Directional',
		name: 'Bi-Directional'
	},
];

export const SCAN_SIDES = [
	{
		id: 'Internal',
		name: 'Internal'
	},
	{
		id: 'External',
		name: 'External'
	},
	{
		id: 'Internal & External',
		name: 'Internal & External'
	},
];

export const EDITABLE_PROJECT_STATUS = [
	PROJECT_STATUS.UNDER_CORRECTION,
	PROJECT_STATUS.PENDING,
];

export const PEC_SCAN_LOCATIONS = [
	{
		label: 'Pipe body',
		value: 'Pipe body',
	},
	{
		label: 'Elbow',
		value: 'Elbow',
	},
	{
		label: 'Nozzle',
		value: 'Nozzle',
	},
	{
		label: 'Body',
		value: 'Body',
	},
	{
		label: 'Dish end (T)',
		value: 'Dish end (T)',
	},
	{
		label: 'Dish end (A)',
		value: 'Dish end (A)',
	},
	{
		label: 'Dish end (F)',
		value: 'Dish end (F)',
	},
	{
		label: 'Dish end (S)',
		value: 'Dish end (S)',
	},
	{
		label: 'Dish end (P)',
		value: 'Dish end (P)',
	},
];

export const RISK_RATING_TYPES = {
	LOW: 'Low',
	MEDIUM: 'Medium',
	HIGH: 'High',
	SEVERE: 'Severe',
} as const;

export const ANOMALY_CHANCE_OF_OCCURRENCE_TYPES = {
	RARE: 'Rare',
	UNLIKELY: 'Unlikely',
	POSSIBLE: 'Possible',
	LIKELY: 'Likely',
	CERTAINLY: 'Certainly',
} as const;

export const ANOMALY_PEOPLE_AT_RISK_TYPES = {
	PEOPLE: 'People',
	PROPERTY: 'Property',
} as const;

export const ANOMALY_PEOPLE_AT_RISK_OPTIONS = [
	ANOMALY_PEOPLE_AT_RISK_TYPES.PEOPLE,
	ANOMALY_PEOPLE_AT_RISK_TYPES.PROPERTY,
];

export const ANOMALY_CHANCE_OF_OCCURRENCE_OPTIONS = [
	ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.RARE,
	ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.UNLIKELY,
	ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.POSSIBLE,
	ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.LIKELY,
	ANOMALY_CHANCE_OF_OCCURRENCE_TYPES.CERTAINLY,
];

export const ANOMALY_INTERVENTION_TYPES = [
	{
		label: 'Civil',
		value: 'Civil',
	},
	{
		label: 'Mechanical',
		value: 'Mechanical',
	},
	{
		label: 'Electrical',
		value: 'Electrical',
	},
];

export const RISK_RATING_OPTIONS = [
	RISK_RATING_TYPES.LOW,
	RISK_RATING_TYPES.MEDIUM,
	RISK_RATING_TYPES.HIGH,
	RISK_RATING_TYPES.SEVERE,
];

export const ALPHABET = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');

export const ALPHABET_OPTIONS = ALPHABET.map((letter) => ({
	id: letter,
	name: letter,
}));

export const HTTP_STATUS_CODES = {
	CREATED: 201,
	UPDATED: 200,
};

export const HTTP_SUCCESS_CODES = [
	HTTP_STATUS_CODES.CREATED,
	HTTP_STATUS_CODES.UPDATED,
];

export const FORBIDDEN_VALUES = [
	'NA',
	'N/A'
];

export const FILE_UPLOAD_ERRORS = {
	PHOTO_CAPTION_UPDATE_COMMENT: 'Please change the photo comment!',
	DUPLICATE_CAPTIONS_COMMENT: 'No duplicate captions allowed: No file extensions or duplicate comments allowed',
	PHOTO_PREFIX_FOUND: 'Please remove the unnecessary prefix: Photo'
};

export const PIPING_SUMMARY_KEY = {
	PIPE_BODY: 'Pipe Body / Elbow',
	FLANGE: 'Flange & Fasteners',
	VALVE: 'Valve',
	UNDER_PIPE_SUPPORT: 'Under Pipe Support',
	PIPE_SUPPORT: 'Pipe Support',
	SMALL_BORE_PIPE: 'Small Bore Pipe',
	DRAIN_BOX: 'Drain Box',
};

export const EQUIPMENT_SUMMARY_KEY = {
	BODY: 'Body',
	FLANGE: 'Flange',
	FASTENERS: 'Fasteners',
	VALVE: 'Valve',
	UNDER_SUPPORT: 'Under Support',
	SUPPORT: 'Support',

	PIPE: 'Pipe',
	GUAGE: 'Guage',
	DISH: 'Dish',
	TOP_DISH: 'Top Dish',
	BOTTOM_DISH: 'Bottom Dish',
	NOZZLE: 'Nozzle',
	NAME_PLATE: 'Name Plate',
	SKIRT: 'Skirt',
	STRUCTURE: 'Structure',
	EXTERNAL_PROTECTION: 'External Protection',
	INSULATION: 'Insulation',
};

export const EQUIPMENT_OPTIONS = [
	{
		value: 'Noise meter ltd (101175908)',
		label: 'Noise meter ltd (101175908)',
		serialNumber: '101175908',
		model: '(Epoch XT)',
	},
	{
		value: 'Olympus NDT, Inc (150898706)',
		label: 'Olympus NDT, Inc (150898706)',
		serialNumber: '150898706',
		model: 'Epoch 600',
	},
	{
		value: 'Modsonic Instruments MFG.CO (E334747-0412)',
		label: 'Modsonic Instruments MFG.CO (E334747-0412)',
		serialNumber: 'E334747-0412',
		model: 'Einstein-II DGS',
	},
	{
		value: 'Olympus NDT, Inc. (140701505)',
		label: 'Olympus NDT, Inc. (140701505)',
		serialNumber: '140701505',
		model: 'Epoch 600',
	},
	{
		value: 'Olympus NDT, Inc (N500X1010R040766)',
		label: 'Olympus NDT, Inc (N500X1010R040766)',
		serialNumber: 'N500X1010R040766',
		model: 'Nortec 500D',
	},
	{
		value: 'Olympus NDT, Inc (N500X11290T081564)',
		label: 'Olympus NDT, Inc (N500X11290T081564)',
		serialNumber: 'N500X11290T081564',
		model: 'Nortec 500 Single',
	},
	{
		value: 'Cygnus Instruments (20242)',
		label: 'Cygnus Instruments (20242)',
		serialNumber: '20242',
		model: 'Cygnus 6 Plus',
	},
	{
		value: 'Cygnus Instruments (20236)',
		label: 'Cygnus Instruments (20236)',
		serialNumber: '20236',
		model: 'Cygnus 6 Plus',
	},
	{
		value: 'Cygnus Instruments (20494)',
		label: 'Cygnus Instruments (20494)',
		serialNumber: '20494',
		model: 'Cygnus 6 Plus',
	},
	{
		value: 'Cygnus Instruments (27921)',
		label: 'Cygnus Instruments (27921)',
		serialNumber: '27921',
		model: 'Cygnus 1 intrinsically safe',
	},
	{
		value: 'Cygnus Instruments (6919)',
		label: 'Cygnus Instruments (6919)',
		serialNumber: '6919',
		model: 'Cygnus 6 Plus',
	},
	{
		value: 'Cygnus Instruments (20242)',
		label: 'Cygnus Instruments (20242)',
		serialNumber: '20242',
		model: 'Cygnus 6 Plus',
	},
	{
		value: 'Cygnus Instruments (G3198)',
		label: 'Cygnus Instruments (G3198)',
		serialNumber: 'G3198',
		model: 'G3198(RSG/OP/IE/0811/001)',
	},
	{
		value: 'Cygnus Instruments (GM55)',
		label: 'Cygnus Instruments (GM55)',
		serialNumber: 'G3198(RSG/OP/IE/0811/001)',
		model: 'GM55',
	},
	{
		value: 'Cygnus Instruments (GM55)',
		label: 'Cygnus Instruments (GM55)',
		serialNumber: 'G3198(RSG/OP/IE/0811/001)',
		model: 'GM55',
	},
	{
		value: 'H03123022101 (HUATEC TG330)',
		label: 'H03123022101 (HUATEC TG330)',
		serialNumber: 'H03123022101',
		model: 'TG330',
	},
	{
		value: 'H03123022103 (HUATEC TG330)',
		label: 'H03123022103 (HUATEC TG330)',
		serialNumber: 'H03123022103',
		model: 'TG330',
	},
	{
		value: 'H03224022101 (HUATEC TG330)',
		label: 'H03224022101 (HUATEC TG330)',
		serialNumber: 'H03224022101',
		model: 'TG330',
	},
	{
		value: 'Benetech GM-100 (LH: 3031943)',
		label: 'Benetech GM-100 (LH: 3031943)',
		serialNumber: 'LH: 3031943',
		model: 'GM-100'
	},
	{
		value: 'Huatec-Yoke HCDX-Y2 240V AC (25030406)',
		label: 'Huatec-Yoke HCDX-Y2 240V AC (25030406)',
		serialNumber: '25030406',
		model: 'GM-100'
	},
	{
		value: 'Huatec-Yoke HCDX-Y2 240V AC (25030407)',
		label: 'Huatec-Yoke HCDX-Y2 240V AC (25030407)',
		serialNumber: '25030407',
		model: 'GM-100'
	},
	{
		value: 'Modal: Olympus 39 DL Plus (240008906)',
		label: 'Modal: Olympus 39 DL Plus (240008906)',
		serialNumber: '240008906',
		model: 'GM-100'
	},
	{
		value: 'Modal: Olympus 39 DL Plus (240008706)',
		label: 'Modal: Olympus 39 DL Plus (240008706)',
		serialNumber: '240008706',
		model: 'GM-100'
	},
	{
		value: 'MPI Yoke - HUATEC (HCDX-Y2) (25050771)',
		label: 'MPI Yoke - HUATEC (HCDX-Y2) (25050771)',
		serialNumber: '25050771',
		model: 'HCDX-Y2'
	},
	{
		value: 'UT Machine - HUATEC (FD620) (A00325050906)',
		label: 'UT Machine - HUATEC (FD620) (A00325050906)',
		serialNumber: 'A00325050906',
		model: 'FD620'
	},
	{
		value: 'OMNIX4 (Evident) (QC-0101340)',
		label: 'OMNIX4 (Evident) (QC-0101340)',
		serialNumber: 'QC-0101340',
		model: 'OMNIX4'
	},
];

export const COUPLANT_OPTIONS = [
	{
		id: 'Grease',
		name: 'Grease',
	},
	{
		id: 'Water',
		name: 'Water',
	},
	{
		id: 'Oil',
		name: 'Oil',
	},
	{
		id: 'Paste',
		name: 'Paste',
	},
];

export const LIMITATION_OPTIONS = [
	{
		label: 'Edge Effect',
		value: 'Edge Effect',
	},
	{
		label: 'Restriction for scan',
		value: 'Restriction for scan',
	},
	{
		label: 'Support',
		value: 'Support',
	},
	{
		label: 'Vibration',
		value: 'Vibration',
	},
	{
		label: 'Electric signal interference',
		value: 'Electric signal interference',
	},
	{
		label: 'Lift off effect',
		value: 'Lift off effect',
	},
	{
		label: 'Weld',
		value: 'Weld',
	},
	{
		label: 'No limitation',
		value: 'No limitation',
	},
];

export const DEFECTS = [
	{
		id: 'Yes',
		name: 'Yes',
	},
	{
		id: 'No',
		name: 'No',
	},
];

export const IMAGE_TYPES = [
	{
		id: 'Image Plate',
		name: 'Image Plate',
	},
	{
		id: 'Digital Film',
		name: 'Digital Film',
	},
	{
		id: 'Foldable Image Plate',
		name: 'Foldable Image Plate',
	},
	{
		id: 'Conventional Film',
		name: 'Conventional Film',
	},
];

export const IMAGE_TYPE_SELECTION = [
	{
		id: 'Direct Image',
		name: 'Direct Image',
	},
	{
		id: 'Inverse Image',
		name: 'Inverse Image',
	},
];

export const IQI_TYPES = [
	{
		id: 'Wired',
		name: 'Wired',
	},
	{
		id: 'Hole',
		name: 'Hole',
	},
];

export const RT_TECHNIQUES = [
	{
		id: 'Single-Wall Single-Image (SWSI)',
		name: 'Single-Wall Single-Image (SWSI)'
	},
	{
		id: 'Double-Wall Single-Image (DWSI)',
		name: 'Double-Wall Single-Image (DWSI)'
	},
	{
		id: 'Double-Wall Double-Image (DWDI)',
		name: 'Double-Wall Double-Image (DWDI)'
	},
];

export const SOURCES = [
	{
		id: 'X-Ray',
		name: 'X-Ray',
		unit: 'KVA',
	},
	{
		id: 'Pulsed X-Ray',
		name: 'Pulsed X-Ray',
		unit: 'KVA',
	},
	{
		id: 'IR192',
		name: 'IR192',
		unit: 'Curie'
	},
];

export const SOURCE_MANUFACTURERS = [
	{
		id: 'GE',
		name: 'GE',
	},
	{
		id: 'HUATEC',
		name: 'HUATEC',
	},
	{
		id: 'OTHER',
		name: 'OTHER',
	},
];

export const SOURCE_ACTIVITY = _.range(1, 351).map((value) => ({
	id: value,
	name: value,
}));

export const SDD_OPTIONS = _.range(1, 1001).filter((value) => value % 5 === 0).map((value) => ({
	id: value,
	name: value,
}));

export const EXTENDABLE_MODULES = {
	ADHOC: 'AD-HOC',
};

export const FVI_SCHEMA = {
	id: Joi.number().optional().label('S/N'),

	location: Joi.string().required().label('Anomaly Location'),

	description: Joi.array()
		.min(1)
		.items(
			Joi.object({
				label: Joi.string().required(),
				value: Joi.string().required(),
				__isNew__: Joi.boolean().optional(),
			})
		)
		.required()
		.label('Anomaly / Inspection Description'),

	recommendation: Joi.array()
		.min(1)
		.items(
			Joi.object({
				label: Joi.string().required(),
				value: Joi.string().required(),
				__isNew__: Joi.boolean().optional(),
			})
		)
		.required()
		.label('Remark & Recommendation'),

	class: Joi.number().min(0).required().label('Class'),

	closeImage: Joi.array()
		.when('location', {
			is: Joi.valid(
				PIPING_SUMMARY_KEY.UNDER_PIPE_SUPPORT,
				PIPING_SUMMARY_KEY.PIPE_SUPPORT
			),
			then: Joi.array().min(3).max(3).required(),
			otherwise: Joi.array().min(1).max(1).required(),
		})
		.label('Close Image'),

	generalImage: Joi.array()
		.min(1)
		.max(1)
		.required()
		.label('General Image'),
};

export const DEFAULT_FVI_SCHEMA = {
	location: Joi.string().required().label('Anomaly Location'),
	description: Joi.array()
		.min(1)
		.items(
			Joi.object({
				label: Joi.string().required(),
				value: Joi.string().required(),
				__isNew__: Joi.boolean().optional(),
			})
		)
		.required()
		.label('Anomaly / Inspection Description'),
	recommendation: Joi.array()
		.min(1)
		.items(
			Joi.object({
				label: Joi.string().required(),
				value: Joi.string().required(),
				__isNew__: Joi.boolean().optional(),
			})
		)
		.required()
		.label('Remark & Recommendation'),
	class: Joi.number().min(0).required().label('Class'),
	generalImage: Joi.array().min(1).max(1).required().label('General Image'),
	closeImage: Joi.array().min(1).max(1).required().label('Close Image'),
};

export const TECHNIQUES = ['Shear Wave', 'Longitudinal Wave', 'Pitch Catch Method'];

export const OFFLINE_TOKEN: string = 'adm_offline';
export const OTHER_DESCRIPTION = 'Others';
export const LOGIN_ENDPOINT = '/user/login';
export const CACHE_LOGS_ENDPOINT = '/project/get-cache';
export const IMAGE_TYPE_PREFIX = 'image/';
export const PNG_IMAGE_EXTENSION = 'png';
export const JPG_IMAGE_EXTENSION = 'jpg';
export const EXCEL_APPLICATION_EXTENSION = 'xls';
export const PDF_APPLICATION_EXTENSION = 'pdf';
export const APPLICATION_TYPE_PREFIX = 'application/';
export const PDF_APPLICATION_TYPE = 'application/pdf';
export const UTM_READING_ITEM_AFFIX = 'mm minimum thickness was noticed on';
export const EXCEL_APPLICATION_TYPE =
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const DEFAULT_REPORT_TITLE = 'Integrity Data Management System (IDMS)';
export const ASSIGNING_A_CONTACT_PERSON =
	'https://res.cloudinary.com/dermo1wjq/image/upload/v1715788749/Assigning_a_project_umujvt.pdf';
export const INTERNAL_USER_GUIDE_URL =
	'https://res.cloudinary.com/dvqbyseti/image/upload/v1683285070/user-guide_cwzqw1.pdf';
export const EXTERNAL_USER_GUIDE_URL =
	'https://rsidms.com/IDMS_Customer_User_Guide.pdf';

export const CACHE_EXPIRY_IN_MINUTES = 30;
export const BYTE_SIZE = 1_024_000;
export const AUTOSAVE_INTERVAL = 180_000;
export const NETWORK_TIMEOUT = 300_000;
export const MAX_IMG_SIZE_IN_BYTES = 16_000_000;
export const MAX_PDF_UPLOAD_SIZE = 100_000_000;
export const MAX_IMAGES_PER_ITEM = 6;
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 40;
export const MAX_INSPECTED_ITEMS_PER_REPORT = 500;
export const MAX_FILE_UPLOAD_SIZE = 100 * 1024 * 1024;
export const SUPER_ADMIN_ROLE_NAME = 'super admin';
export const MAX_FILE_SIZE_MB = 0.3;
export const MAX_ANOMALY_REPORT_PER_PAGE = 2;
export const REPORT_ITEMS_SAFE_LIMIT = 30;
export const LETTER_LIMIT_PER_A4_PAGE = 2300;
export const MAX_PAGE_WORD_COUNT = 550;

export const APP_DATE_FORMAT = 'DD/MM/YYYY';
export const DATEPICKER_DATE_FORMAT = 'dd/MM/yyyy';
export const DAYJS_VALID_FORMAT = 'YYYY-MM-DD';
export const FULL_DATE_FORMAT = 'MMMM DD, YYYY'

// export const NEW_UTM_KICKOFF_DATE = '2024-10-30';
export const NEW_UTM_KICKOFF_DATE = '2023-10-30';
export const MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE = 6;

export const DRAGGABLE_ID = "application/idms";
export const PAGE_SIZE_MAX = 100;